import { Dispatch, SetStateAction, memo } from 'react';
import { IOrder, IOrderPaymentLines } from '../../interfaces/Order';
import { ActionIcon, Button, Grid, Menu, Text, Tooltip, rem } from '@mantine/core';
import moment from 'moment';
import { type MRT_PaginationState } from 'mantine-react-table';
import { useTranslation } from 'react-i18next';
import Table from '../Table';
import { formatMoneyString, getCustomerFullName } from '../../utils/strings';
import OrderStatuses from '../../enum/OrderStatuses';
import {
  IconArrowsLeftRight,
  IconCircleCheck,
  IconCircleDashedCheck,
  IconCurrencyDollar,
  IconEdit,
  IconMenu2,
  IconPhoto,
  IconSearch,
  IconSettings,
  IconShirt,
  IconTrash,
  IconWash
} from '@tabler/icons-react';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { useHistory } from 'react-router-dom';
import { IStore } from '../../interfaces/Store';
import { getOrderCardColorsByAmountDueAndStatus, normalizeOrderScheduleTimeValue } from '../../utils/ordersUtils';
import { getAllPieces } from '../../utils/helpers/getAllPieces';
import useCanEditOrders from '../../hooks/useCanEditOrders';
import useCanValidateStoreReception from '../../hooks/useCanValidateStoreReception';
import {
  STORE_RECEPTION_VALIDATION_QUERY_MODE,
  canShowValidateStoreReceptionAction,
  getOrderStoreId
} from '../../utils/storeReceptionValidation';

type ActionMenuProps = {
  order: IOrder;
  searchParamStoreId: string;
  storeReceptionValidationEnabled: boolean;
  showPaymentLinesModal: (order: IOrder) => void;
  showOrderProductsModal: (order: IOrder) => void;
  onOrderDelivered: ({
    orderId,
    sequence,
    storeId
  }: {
    orderId: string;
    sequence: string;
    storeId: string;
  }) => Promise<void>;
  onOrderCleaning: ({ sequence, storeId }: { sequence: string; storeId: string }) => Promise<void>;
  onOrderCancelled: ({ sequence, storeId }: { sequence: string; storeId: string }) => Promise<void>;
};

const ActionMenu = ({
  order,
  searchParamStoreId,
  storeReceptionValidationEnabled,
  showPaymentLinesModal,
  showOrderProductsModal,
  onOrderDelivered,
  onOrderCleaning,
  onOrderCancelled
}: ActionMenuProps) => {
  const { t } = useTranslation(['OrdersPage', 'FieldName']);
  const history = useHistory();
  const canEditOrders = useCanEditOrders();
  const canValidateStoreReception = useCanValidateStoreReception();
  const orderStoreId = getOrderStoreId(order, searchParamStoreId);

  const rowActions = [
    <Menu.Item
      onClick={() => showPaymentLinesModal(order)}
      leftSection={<IconCurrencyDollar style={{ width: rem(14), height: rem(14) }} />}>
      {t('OrdersPage:paymentLinesModal.tooltip')}
    </Menu.Item>,
    <Menu.Item
      onClick={() => showOrderProductsModal(order)}
      leftSection={<IconShirt style={{ width: rem(14), height: rem(14) }} />}>
      {t('OrdersPage:productsModal.tooltip')}
    </Menu.Item>,
    <Menu.Item
      hidden={
        !canShowValidateStoreReceptionAction({
          order,
          canValidateStoreReception,
          storeReceptionValidationEnabled
        })
      }
      onClick={() =>
        history.push(
          `/orders/${order._id}?storeId=${orderStoreId}&mode=${STORE_RECEPTION_VALIDATION_QUERY_MODE}`,
          {
            storeID: orderStoreId
          }
        )
      }
      leftSection={<IconCircleDashedCheck style={{ width: rem(14), height: rem(14) }} />}>
      {t('OrdersPage:validateStoreReception')}
    </Menu.Item>,
    <Menu.Item
      disabled={!canEditOrders}
      onClick={() =>
        history.push(`/orders/${order._id}?storeId=${searchParamStoreId}`, {
          storeID: searchParamStoreId
        })
      }
      leftSection={<IconEdit style={{ width: rem(14), height: rem(14) }} />}>
      {t('CommonButtons:edit')}
    </Menu.Item>
  ];

  if (order.status === OrderStatuses.DELIVERING) {
    rowActions.push(
      <Menu.Item
        onClick={() =>
          ConfirmationModal({
            title: t('Notifications:CRUD.deliveredConfirmation'),
            onConfirm: () =>
              onOrderDelivered({
                orderId: order._id || '',
                sequence: order.sequence || '',
                storeId: order.createdIn?._id || ''
              }),
            onCancel: () => {}
          })
        }
        leftSection={<IconCircleCheck style={{ width: rem(14), height: rem(14) }} />}>
        Pedido Entregado
      </Menu.Item>
    );
  } else if (order.status === OrderStatuses.PICKINGUP) {
    rowActions.push(
      <Menu.Item
        onClick={() =>
          ConfirmationModal({
            title: t('Notifications:CRUD.cleaningConfirmation'),
            onConfirm: () =>
              onOrderCleaning({
                sequence: order.sequence || '',
                storeId: order.createdIn?._id || ''
              }),
            onCancel: () => {}
          })
        }
        leftSection={<IconWash style={{ width: rem(14), height: rem(14) }} />}>
        Pedido en Proceso
      </Menu.Item>
    );
  }

  rowActions.push(
    <Menu.Item
      onClick={() =>
        ConfirmationModal({
          title: t('Notifications:CRUD.cancelConfirmation'),
          onConfirm: () =>
            onOrderCancelled({
              sequence: order.sequence || '',
              storeId: order.createdIn?._id || ''
            }),
          onCancel: () => {}
        })
      }
      leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}>
      Cancelar Pedido
    </Menu.Item>
  );

  return (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <ActionIcon variant="transparent">
          <IconMenu2 style={{ width: '70%', height: '70%' }} stroke={1.5} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>{...rowActions}</Menu.Dropdown>
    </Menu>
  );
};
export default memo(function OrdersTableView({
  orders = [],
  totalRowsCount = 0,
  pages = 0,
  isError = false,
  isLoading = false,
  pagination,
  setPagination,
  searchParamStoreId,
  storeReceptionValidationEnabled,
  onOrderDelivered,
  onOrderCleaning,
  onOrderCancelled,
  // onSendEmailReceipt,
  // onPrintTicket,
  showPaymentLinesModal,
  showOrderProductsModal
}: {
  orders: any;
  totalRowsCount: any;
  pages: any;
  isError: boolean;
  isLoading: boolean;
  pagination: MRT_PaginationState;
  setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
  searchParamStoreId: string;
  storeReceptionValidationEnabled: boolean;
  onOrderDelivered: ({
    orderId,
    sequence,
    storeId
  }: {
    orderId: string;
    sequence: string;
    storeId: string;
  }) => Promise<void>;
  onOrderCleaning: ({ sequence, storeId }: { sequence: string; storeId: string }) => Promise<void>;
  onOrderCancelled: ({ sequence, storeId }: { sequence: string; storeId: string }) => Promise<void>;
  // onSendEmailReceipt: (orderId: string) => Promise<void>;
  // onPrintTicket: (order: IOrder) => Promise<void>;
  showPaymentLinesModal: (order: IOrder) => void;
  showOrderProductsModal: (order: IOrder) => void;
}) {
  const { t } = useTranslation(['FieldName']);
  const columns = [
    {
      header: t('FieldName:actions'),
      size: 50, //small column
      accessorFn: (row) => {
        return (
          <ActionMenu
            order={row}
            searchParamStoreId={searchParamStoreId}
            storeReceptionValidationEnabled={storeReceptionValidationEnabled}
            showPaymentLinesModal={showPaymentLinesModal}
            showOrderProductsModal={showOrderProductsModal}
            onOrderDelivered={onOrderDelivered}
            onOrderCleaning={onOrderCleaning}
            onOrderCancelled={onOrderCancelled}
          />
        );
      }
    },
    {
      header: t('FieldName:pieces'),
      accessorFn: (row) => {
        const orderProducts = row?.products || [];
        const orderBuyAndGerProducts = row?.buyAndGetProducts || [];
        const allPieces = getAllPieces(orderProducts, orderBuyAndGerProducts);
        return allPieces;
      }
    },
    {
      header: t('FieldName:name'),
      accessorFn: (row) => getCustomerFullName(row.customer.name, row.customer.lastName)
    },
    {
      header: t('FieldName:total'),
      accessorFn: (row) => formatMoneyString((row.total || 0).toFixed(2))
    },
    {
      header: t('FieldName:amountDue'),
      accessorFn: (row) =>
        formatMoneyString(
          (
            row.total -
              row.paymentLines?.reduce(
                (prev: number, next: IOrderPaymentLines) => prev + (next?.amountPaid || 0),
                0
              ) || 0
          ).toFixed(2)
        )
    },
    {
      header: t('FieldName:status'),
      accessorFn: (row) => t(`FieldName:orderStatuses.${OrderStatuses[row.status.toUpperCase()]}`)
    },
    {
      header: t('FieldName:pickupDate'),
      accessorFn: (row) => {
        let pickUpData = t('FieldName:pickupNotRequired');
        if (row.pickup && row.pickupInfo) {
          const pickupDate = moment(row.pickupInfo.date).format('DD/MM/YYYY');
          const pickupFromTime = normalizeOrderScheduleTimeValue(row.pickupInfo.fromTime);
          const pickuptoTime = normalizeOrderScheduleTimeValue(row.pickupInfo.toTime);
          pickUpData = `${pickupDate} ${pickupFromTime} - ${pickuptoTime}`;
        }
        return pickUpData;
      }
    },
    {
      header: t('FieldName:deliveryDate'),
      accessorFn: (row) => {
        let deliveryData = t('FieldName:pickupNotRequired');
        if (row.deliveryInfo) {
          const deliveryDate = moment(row.deliveryInfo.date).format('DD/MM/YYYY');
          const deliveryFromTime = row.deliveryInfo.fromTime
            ? normalizeOrderScheduleTimeValue(row.deliveryInfo.fromTime)
            : '';
          const deliverytoTime = row.deliveryInfo.toTime
            ? normalizeOrderScheduleTimeValue(row.deliveryInfo.toTime)
            : '';
          deliveryData = `${deliveryDate} ${deliveryFromTime} - ${deliverytoTime}`;
        }
        return deliveryData;
      }
    },
    {
      header: t('FieldName:createdDate'),
      accessorFn: (row) => moment(row.createdDate).format('DD/MM/YYYY HH:mm') || ''
    },
    {
      header: t('FieldName:notes'),
      accessorFn: (row) => row.notes || ''
    }
  ];

  return (
    <>
      <Grid gutter="md">
        <Grid.Col span={12}>
          <Table
            tableColumns={columns}
            pagination={pagination}
            setPagination={setPagination}
            tableData={orders}
            isError={isError}
            paginationIsLoading={isLoading}
            numPages={pages}
            totalRowsCount={totalRowsCount}
            overrideTableConfigs={{
              mantineTableBodyRowProps: ({ row }: { row: any }) => {
                const paymentLinesTotal = row.original?.paymentLines?.reduce(
                  (prev: number, next: IOrderPaymentLines) => {
                    return prev + (next?.amountPaid || 0);
                  },
                  0
                );
                const amountDue = row.original?.total - paymentLinesTotal;
                const customColor = getOrderCardColorsByAmountDueAndStatus({
                  amountDue,
                  highlightOrderCardsByPaymentPending: (row.original?.createdIn as Partial<IStore>)
                    ?.ordersDisplayConfig?.highlightOrderCardsByPaymentPending,
                  status: row.original?.status
                });

                return {
                  style: {
                    backgroundColor: customColor?.headerBackgroundColor,
                    color: customColor?.headerTextColor
                  }
                };
              }
            }}
          />
        </Grid.Col>
      </Grid>
    </>
  );
});
